/**
 * Rule-based text for `/overview` (PLAN-FASE2.md § WP-06): the
 * "Belangrijkste ontwikkelingen"/"Acties voor HR" cards are **templated on
 * the numbers already on the page**, picked by fixed thresholds — no AI
 * endpoint in this WP, same approach WP-02's `metrics.ts` (HR-action
 * suggestions) and WP-03's min-group-size gate already use. Kept as pure
 * functions, independent of `react-i18next`'s `t()` (and of any locale —
 * `mostActiveWeekday` in particular is resolved by the caller, not here),
 * so they're plain-unit-testable: the page passes each returned key + params
 * through `t()` itself.
 *
 * The "Hoe gaat het met [bedrijf]?" summary itself reuses Vitality's own
 * `buildInsightText` (`@/features/vitality/insight`) rather than a second,
 * differently-shaped template here — same numbers, same sentence style,
 * built once.
 */

import type { CardData } from '@/features/vitality/deriveCards';
import type { FunnelStep } from '@/features/participation/api';

export interface OverviewInputs {
  vitalityScore: CardData | null;
  stress: CardData | null;
  sleep: CardData | null;
  /** `active_30d` funnel step, or `null` when participation has no data. */
  activeParticipants: FunnelStep | null;
  /** `invited` funnel step, needed alongside `activated` for the "not yet
   * activated" action suggestion. */
  invited: FunnelStep | null;
  activated: FunnelStep | null;
  needsReactivation: FunnelStep | null;
  /** The weekday name (already resolved to the caller's locale via
   * `mostActiveWeekday()` in `@/features/vitality/series`) with the highest
   * average activity, or `null` when there's no activity data to rank. */
  mostActiveWeekday: string | null;
  /** Share of the company's invited headcount that has joined at least one
   * challenge, rounded to a whole percent. `null` when there's no invited
   * headcount to divide by. */
  challengeParticipationPct: number | null;
  /** The first currently-active challenge, if any. */
  activeChallenge: { title: string } | null;
  /** The first pilot sitting in an evaluation-ready status, if any. */
  pilotNeedingEvaluation: { id: string; name: string; phase: 'interim' | 'final' } | null;
}

/** One templated sentence, already carrying its own translation key and
 * interpolation params — the page resolves it with `t(key, params)`. */
export interface SummaryLine {
  key: string;
  params?: Record<string, string | number>;
}

/** One "Belangrijkste ontwikkelingen" entry: a short bold heading plus a
 * longer description line, rendered as its own sub-card. `id` doubles as
 * the React key and as the lookup into the page's per-item icon map. */
export interface DevelopmentItem {
  id: string;
  title: SummaryLine;
  body: SummaryLine;
}

/** One "Acties voor HR" entry: a title, a subtitle naming the concrete next
 * step, and an optional link to where that action is carried out (omitted
 * for a suggestion with no natural page to visit). */
export interface ActionItem {
  id: string;
  title: SummaryLine;
  subtitle: SummaryLine;
  linkTo?: string;
}

/** "Belangrijkste ontwikkelingen": kansen/signalen, one per breached
 * threshold — empty when nothing crosses one, rendered as an `EmptyState`
 * by the page rather than here. */
export function buildDevelopments(inputs: OverviewInputs): DevelopmentItem[] {
  const items: DevelopmentItem[] = [];

  if (inputs.stress?.level === 'opportunity') {
    items.push({
      id: 'stress',
      title: { key: 'overview.developments.stress.title' },
      body: { key: 'overview.developments.stress.body' },
    });
  }
  if (inputs.sleep?.level === 'opportunity') {
    items.push({
      id: 'sleep',
      title: { key: 'overview.developments.sleep.title' },
      body: { key: 'overview.developments.sleep.body' },
    });
  }
  if (inputs.vitalityScore?.level === 'good') {
    items.push({
      id: 'vitalityGood',
      title: { key: 'overview.developments.vitalityGood.title' },
      body: { key: 'overview.developments.vitalityGood.body' },
    });
  }
  if (inputs.needsReactivation !== null && inputs.needsReactivation.count > 0) {
    items.push({
      id: 'needsReactivation',
      title: {
        key: 'overview.developments.needsReactivation.title',
        params: { count: inputs.needsReactivation.count },
      },
      body: { key: 'overview.developments.needsReactivation.body' },
    });
  }
  if (inputs.mostActiveWeekday !== null) {
    items.push({
      id: 'mostActiveWeekday',
      title: {
        key: 'overview.developments.mostActiveWeekday.title',
        params: { weekday: inputs.mostActiveWeekday },
      },
      body: { key: 'overview.developments.mostActiveWeekday.body' },
    });
  }
  // `> 0` rather than `!== null`: a 0%-participation figure isn't an
  // "ontwikkeling" worth calling out, just an absence of one.
  if (inputs.challengeParticipationPct !== null && inputs.challengeParticipationPct > 0) {
    items.push({
      id: 'challengeParticipation',
      title: {
        key: 'overview.developments.challengeParticipation.title',
        params: { pct: inputs.challengeParticipationPct },
      },
      body: { key: 'overview.developments.challengeParticipation.body' },
    });
  }

  return items;
}

/** "Acties voor HR": a recommended next step per signal above, e.g. "106
 * medewerkers nog niet geactiveerd" → "Stuur herinnering" (the flowchart's
 * own example) — plus two structural actions (an active challenge to plan
 * the next one around, a pilot ready for evaluation) that don't depend on
 * any threshold being crossed, so this card isn't empty just because
 * nothing in the numbers needs attention right now. */
export function buildActions(inputs: OverviewInputs): ActionItem[] {
  const items: ActionItem[] = [];

  if (inputs.invited !== null && inputs.activated !== null) {
    const notActivated = inputs.invited.count - inputs.activated.count;
    if (notActivated > 0) {
      items.push({
        id: 'sendReminder',
        title: {
          key: 'overview.actions.sendReminder.title',
          params: { count: notActivated },
        },
        subtitle: { key: 'overview.actions.sendReminder.subtitle' },
        linkTo: '/users',
      });
    }
  }

  if (inputs.needsReactivation !== null && inputs.needsReactivation.count > 0) {
    items.push({
      id: 'reactivate',
      title: {
        key: 'overview.actions.reactivate.title',
        params: { count: inputs.needsReactivation.count },
      },
      subtitle: { key: 'overview.actions.reactivate.subtitle' },
      linkTo: '/users',
    });
  }

  if (inputs.stress?.level === 'opportunity') {
    items.push({
      id: 'stressCheckIn',
      title: { key: 'overview.actions.stressCheckIn.title' },
      subtitle: { key: 'overview.actions.stressCheckIn.subtitle' },
    });
  }
  if (inputs.sleep?.level === 'opportunity') {
    items.push({
      id: 'sleepReminder',
      title: { key: 'overview.actions.sleepReminder.title' },
      subtitle: { key: 'overview.actions.sleepReminder.subtitle' },
    });
  }

  if (inputs.activeChallenge !== null) {
    items.push({
      id: 'activeChallenge',
      title: {
        key: 'overview.actions.activeChallenge.title',
        params: { name: inputs.activeChallenge.title },
      },
      subtitle: { key: 'overview.actions.activeChallenge.subtitle' },
      linkTo: '/challenges',
    });
  }

  if (inputs.pilotNeedingEvaluation !== null) {
    const { id, name, phase } = inputs.pilotNeedingEvaluation;
    items.push({
      id: 'pilotEvaluation',
      title: {
        key: `overview.actions.pilotEvaluation.${phase}.title`,
        params: { name },
      },
      subtitle: { key: `overview.actions.pilotEvaluation.${phase}.subtitle` },
      linkTo: `/pilots/${id}`,
    });
  }

  return items;
}
